import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import FileUpload from '../../components/FileUpload';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import settingApi from '../../api/settingApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES, SETTING_KEYS, PERMISSIONS } from '../../constants'; // Import PERMISSIONS
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth

const Settings = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { hasPermission } = useAuth(); // Use hasPermission hook
  const [loading, setLoading] = useState(true);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [generatingSitemap, setGeneratingSitemap] = useState(false); // New state for sitemap generation
  const [validationErrors, setValidationErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [formData, setFormData] = useState({
    [SETTING_KEYS.SITE_NAME]: '',
    [SETTING_KEYS.EMAIL]: '',
    [SETTING_KEYS.TEAMS_URL]: '',
    [SETTING_KEYS.UPWORK_URL]: '',
    [SETTING_KEYS.LINKEDIN_URL]: '',
    [SETTING_KEYS.GITHUB_URL]: '',
    [SETTING_KEYS.ANALYTICS_PROPERTY_ID]: '',
    [SETTING_KEYS.ANALYTICS_PROPERTY_CREDENTIALS]: null,
    [SETTING_KEYS.MAIL_HOST]: '',
    [SETTING_KEYS.MAIL_PORT]: '',
    [SETTING_KEYS.MAIL_USERNAME]: '',
    [SETTING_KEYS.MAIL_PASSWORD]: '',
    [SETTING_KEYS.MAIL_ENCRYPTION]: '',
    [SETTING_KEYS.MAIL_FROM_ADDRESS]: '',
    [SETTING_KEYS.MAIL_FROM_NAME]: '',
    [SETTING_KEYS.FAVICON]: undefined,
  });
  const [currentAnalyticsCredentialsPath, setCurrentAnalyticsCredentialsPath] = useState(null);
  const [currentFaviconUrl, setCurrentFaviconUrl] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setGeneralError(null);
      try {
        const response = await settingApi.getSettings();
        if (response.success && response.data) {
          const fetchedSettings = response.data;
          const newFormData = {};
          for (const keyName in SETTING_KEYS) {
            const settingKey = SETTING_KEYS[keyName];
            if (settingKey === SETTING_KEYS.ANALYTICS_PROPERTY_CREDENTIALS) {
              newFormData[settingKey] = null;
              setCurrentAnalyticsCredentialsPath(fetchedSettings[settingKey] || null);
            } else if (settingKey === SETTING_KEYS.FAVICON) {
              newFormData[settingKey] = undefined;
              setCurrentFaviconUrl(fetchedSettings[settingKey] || null);
            }
            else {
              newFormData[settingKey] = fetchedSettings[settingKey] || '';
            }
          }
          setFormData(newFormData);
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        setGeneralError(err.response?.data?.message || 'Failed to load settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target; // Destructure 'checked' here
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : (type === 'file' ? files[0] : value),
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRemoveAnalyticsCredentials = () => {
    setFormData(prev => ({ ...prev, [SETTING_KEYS.ANALYTICS_PROPERTY_CREDENTIALS]: '' }));
    setCurrentAnalyticsCredentialsPath(null);
  };

  const handleRemoveFavicon = () => {
    setFormData(prev => ({ ...prev, [SETTING_KEYS.FAVICON]: null }));
    setCurrentFaviconUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const dataToSend = new FormData();
    dataToSend.append(`_method`, 'PATCH');

    for (const keyName in SETTING_KEYS) {
      const settingKey = SETTING_KEYS[keyName];
      const value = formData[settingKey];

      if (settingKey === SETTING_KEYS.ANALYTICS_PROPERTY_CREDENTIALS || settingKey === SETTING_KEYS.FAVICON) {
        if (value instanceof File) {
          dataToSend.append(`settings[${settingKey}]`, value);
        } else if (value === null) {
          dataToSend.append(`settings[${settingKey}]`, '');
        }
      } else {
        if (value !== undefined && value !== null) {
          dataToSend.append(`settings[${settingKey}]`, value);
        }
      }
    }

    try {
      const response = await settingApi.updateSettings(dataToSend);
      addToast('Settings updated successfully!', 'success');

      const fetchedSettings = response.data;
      const newFormData = {};
      for (const keyName in SETTING_KEYS) {
        const settingKey = SETTING_KEYS[keyName];
        if (settingKey === SETTING_KEYS.ANALYTICS_PROPERTY_CREDENTIALS) {
          newFormData[settingKey] = undefined;
          setCurrentAnalyticsCredentialsPath(fetchedSettings[settingKey] || null);
        } else if (settingKey === SETTING_KEYS.FAVICON) {
          newFormData[settingKey] = undefined;
          setCurrentFaviconUrl(fetchedSettings[settingKey] || null);
        } else {
          newFormData[settingKey] = fetchedSettings[settingKey] || '';
        }
      }
      setFormData(newFormData);

    } catch (err) {
      console.error('Failed to update settings:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update settings. Please try again.', 'error');
        setGeneralError(err.response?.data?.message || 'Failed to update settings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    setGeneralError(null);
    try {
      const response = await settingApi.sendTestEmail();
      if (response.success) {
        addToast(response.message || 'Test email sent successfully!', 'success');
      } else {
        setGeneralError(response.message || 'Failed to send test email.');
        addToast(response.message || 'Failed to send test email.', 'error');
      }
    } catch (err) {
      console.error('Failed to send test email:', err);
      setGeneralError(err.response?.data?.message || 'An unexpected error occurred while sending test email.');
      addToast(err.response?.data?.message || 'Failed to send test email. Please check console for details.', 'error');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    setGeneralError(null);
    try {
      const response = await settingApi.clearCache();
      if (response.success) {
        addToast(response.message || 'Application cache cleared successfully!', 'success');
      } else {
        setGeneralError(response.message || 'Failed to clear application cache.');
        addToast(response.message || 'Failed to clear application cache.', 'error');
      }
    } catch (err) {
      console.error('Failed to clear application cache:', err);
      setGeneralError(err.response?.data?.message || 'An unexpected error occurred while clearing cache.');
      addToast(err.response?.data?.message || 'Failed to clear application cache. Please check console for details.', 'error');
    } finally {
      setClearingCache(false);
    }
  };

  const handleGenerateSitemap = async () => {
    setGeneratingSitemap(true);
    setGeneralError(null);
    try {
      const response = await settingApi.generateSitemap();
      if (response.success) {
        addToast(response.message || 'Sitemap generated successfully!', 'success');
      } else {
        setGeneralError(response.message || 'Failed to generate sitemap.');
        addToast(response.message || 'Failed to generate sitemap.', 'error');
      }
    } catch (err) {
      console.error('Failed to generate sitemap:', err);
      setGeneralError(err.response?.data?.message || 'An unexpected error occurred while generating sitemap.');
      addToast(err.response?.data?.message || 'Failed to generate sitemap. Please check console for details.', 'error');
    } finally {
      setGeneratingSitemap(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">General Settings</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <h2 className="text-2xl font-semibold mt-8 mb-4">General</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput
            label="Site Name"
            name={SETTING_KEYS.SITE_NAME}
            id={SETTING_KEYS.SITE_NAME}
            value={formData[SETTING_KEYS.SITE_NAME]}
            onChange={handleChange}
            placeholder="Site Name"
            error={validationErrors?.[SETTING_KEYS.SITE_NAME]?.[0]}
          />
          <TextInput
            label="Contact Email"
            name={SETTING_KEYS.EMAIL}
            id={SETTING_KEYS.EMAIL}
            value={formData[SETTING_KEYS.EMAIL]}
            onChange={handleChange}
            type="email"
            placeholder="Contact Email"
            error={validationErrors?.[SETTING_KEYS.EMAIL]?.[0]}
          />
          <div className="md:col-span-2">
            <FileUpload
              label="Favicon"
              name={SETTING_KEYS.FAVICON}
              id={SETTING_KEYS.FAVICON}
              onChange={handleChange}
              error={validationErrors?.[SETTING_KEYS.FAVICON]?.[0]}
              currentFileUrl={currentFaviconUrl}
              onRemoveFile={handleRemoveFavicon}
              fileType="image"
              fileName="Favicon"
            />
          </div>
        </div>

        <h2 className="text-2xl font-semibold mt-8 mb-4">Social & External Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput
            label="Teams URL"
            name={SETTING_KEYS.TEAMS_URL}
            id={SETTING_KEYS.TEAMS_URL}
            value={formData[SETTING_KEYS.TEAMS_URL]}
            onChange={handleChange}
            placeholder="Teams URL"
            error={validationErrors?.[SETTING_KEYS.TEAMS_URL]?.[0]}
          />
          <TextInput
            label="Upwork URL"
            name={SETTING_KEYS.UPWORK_URL}
            id={SETTING_KEYS.UPWORK_URL}
            value={formData[SETTING_KEYS.UPWORK_URL]}
            onChange={handleChange}
            placeholder="Upwork URL"
            error={validationErrors?.[SETTING_KEYS.UPWORK_URL]?.[0]}
          />
          <TextInput
            label="LinkedIn URL"
            name={SETTING_KEYS.LINKEDIN_URL}
            id={SETTING_KEYS.LINKEDIN_URL}
            value={formData[SETTING_KEYS.LINKEDIN_URL]}
            onChange={handleChange}
            placeholder="LinkedIn URL"
            error={validationErrors?.[SETTING_KEYS.LINKEDIN_URL]?.[0]}
          />
          <TextInput
            label="GitHub URL"
            name={SETTING_KEYS.GITHUB_URL}
            id={SETTING_KEYS.GITHUB_URL}
            value={formData[SETTING_KEYS.GITHUB_URL]}
            onChange={handleChange}
            placeholder="GitHub URL"
            error={validationErrors?.[SETTING_KEYS.GITHUB_URL]?.[0]}
          />
        </div>

        {hasPermission(PERMISSIONS.MANAGE_SETTINGS) && ( // Conditional rendering for sensitive sections
          <>
            <h2 className="text-2xl font-semibold mt-8 mb-4">Analytics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Analytics Property ID"
                name={SETTING_KEYS.ANALYTICS_PROPERTY_ID}
                id={SETTING_KEYS.ANALYTICS_PROPERTY_ID}
                value={formData[SETTING_KEYS.ANALYTICS_PROPERTY_ID]}
                onChange={handleChange}
                placeholder="Analytics Property ID"
                error={validationErrors?.[SETTING_KEYS.ANALYTICS_PROPERTY_ID]?.[0]}
              />
              <FileUpload
                label="Analytics Property Credentials (JSON file)"
                name={SETTING_KEYS.ANALYTICS_PROPERTY_CREDENTIALS}
                id={SETTING_KEYS.ANALYTICS_PROPERTY_CREDENTIALS}
                onChange={handleChange}
                error={validationErrors?.[SETTING_KEYS.ANALYTICS_PROPERTY_CREDENTIALS]?.[0]}
                currentFileUrl={currentAnalyticsCredentialsPath}
                onRemoveFile={handleRemoveAnalyticsCredentials}
                fileType="json"
              />
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Mail Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Mail Host"
                name={SETTING_KEYS.MAIL_HOST}
                id={SETTING_KEYS.MAIL_HOST}
                value={formData[SETTING_KEYS.MAIL_HOST]}
                onChange={handleChange}
                placeholder="Mail Host"
                error={validationErrors?.[SETTING_KEYS.MAIL_HOST]?.[0]}
              />
              <TextInput
                label="Mail Port"
                name={SETTING_KEYS.MAIL_PORT}
                id={SETTING_KEYS.MAIL_PORT}
                value={formData[SETTING_KEYS.MAIL_PORT]}
                onChange={handleChange}
                type="number"
                placeholder="Mail Port"
                error={validationErrors?.[SETTING_KEYS.MAIL_PORT]?.[0]}
              />
              <TextInput
                label="Mail Username"
                name={SETTING_KEYS.MAIL_USERNAME}
                id={SETTING_KEYS.MAIL_USERNAME}
                value={formData[SETTING_KEYS.MAIL_USERNAME]}
                onChange={handleChange}
                placeholder="Mail Username"
                error={validationErrors?.[SETTING_KEYS.MAIL_USERNAME]?.[0]}
              />
              <TextInput
                label="Mail Password"
                name={SETTING_KEYS.MAIL_PASSWORD}
                id={SETTING_KEYS.MAIL_PASSWORD}
                value={formData[SETTING_KEYS.MAIL_PASSWORD]}
                onChange={handleChange}
                type="password"
                placeholder="Mail Password"
                error={validationErrors?.[SETTING_KEYS.MAIL_PASSWORD]?.[0]}
              />
              <TextInput
                label="Mail Encryption"
                name={SETTING_KEYS.MAIL_ENCRYPTION}
                id={SETTING_KEYS.MAIL_ENCRYPTION}
                value={formData[SETTING_KEYS.MAIL_ENCRYPTION]}
                onChange={handleChange}
                placeholder="Mail Encryption"
                error={validationErrors?.[SETTING_KEYS.MAIL_ENCRYPTION]?.[0]}
              />
              <TextInput
                label="Mail From Address"
                name={SETTING_KEYS.MAIL_FROM_ADDRESS}
                id={SETTING_KEYS.MAIL_FROM_ADDRESS}
                value={formData[SETTING_KEYS.MAIL_FROM_ADDRESS]}
                onChange={handleChange}
                type="email"
                placeholder="Mail From Address"
                error={validationErrors?.[SETTING_KEYS.MAIL_FROM_ADDRESS]?.[0]}
              />
              <TextInput
                label="Mail From Name"
                name={SETTING_KEYS.MAIL_FROM_NAME}
                id={SETTING_KEYS.MAIL_FROM_NAME}
                value={formData[SETTING_KEYS.MAIL_FROM_NAME]}
                onChange={handleChange}
                placeholder="Mail From Name"
                error={validationErrors?.[SETTING_KEYS.MAIL_FROM_NAME]?.[0]}
              />
            </div>
          </>
        )}

        <div className="flex items-center justify-between mt-6">
          <div className="flex space-x-4">
            <Button
              type="submit"
              className="bg-accent-red hover:bg-red-700 text-white"
              loading={loading}
            >
              Update Settings
            </Button>
            <Button
              type="button"
              onClick={handleSendTestEmail}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              loading={sendingTestEmail}
              disabled={loading || sendingTestEmail}
            >
              Send Test Email
            </Button>
            <Button
              type="button"
              onClick={handleClearCache}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              loading={clearingCache}
              disabled={loading || clearingCache}
            >
              Clear Cache
            </Button>
            <Button
              type="button"
              onClick={handleGenerateSitemap}
              className="bg-green-600 hover:bg-green-700 text-white"
              loading={generatingSitemap}
              disabled={loading || generatingSitemap}
            >
              Generate Sitemap
            </Button>
          </div>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Settings;